import type { SearchPagesParams } from "./types";

/**
 * Whether a page still belongs in a list that asked for these params, once its
 * folder has changed.
 *
 * Deliberately conservative: it only answers "no" where the answer is certain
 * from the params alone. Everything else keeps the row and lets the refetch that
 * follows the mutation settle it — a row that lingers for a moment is a smaller
 * wrong than a row that vanishes and comes back.
 *
 *   - no params at all: nothing known, keep it
 *   - isFavorite: that list is folder-agnostic, keep it
 *   - folderIds (the Location filter): those ids are expanded to their subtrees
 *     server-side, and the subtree is not knowable here, so keep it — an exact
 *     match would drop a row moved into a child of a filtered folder
 *   - folderId "all": every folder the user can see, keep it
 *   - folderId set: that folder only, so a row filed elsewhere is gone
 *   - no folderId: the root, which is pages with no folder
 */
export function pageBelongsInList(
  pageFolderId: string | null | undefined,
  params?: SearchPagesParams,
): boolean {
  if (!params) return true;
  if ((params as { isFavorite?: boolean }).isFavorite) return true;

  const folderIds = (params as { folderIds?: string[] }).folderIds;
  if (folderIds && folderIds.length > 0) return true;

  if (params.folderId === "all") return true;
  if (params.folderId) return pageFolderId === params.folderId;
  return !pageFolderId;
}
